import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router-dom';
import {useSearchParams} from 'react-router-dom';
import {useDispatch, useSelector} from 'react-redux';
import {
  useLazyGetCurrentFacilityInspectionsQuery,
  useLazyGetCurrentInspectionQuery,
  useLazyGetInspectionsByTypeQuery,
  useRefuseInspectionsMutation,
  useSaveEquipmentFormMutation,
  useUpdateEquipmentTypeMutation,
  useUpdateFacilityInfoMutation,
} from '../../store/builder/inspections.api';
import {useErrorHandler} from '../../global/ErrorsHandler';
import {toast} from 'react-toastify';
import {setLoader} from '../../store/builder/reducer';
import Wrapper from '../../components/Wrapper';
import {Helmet} from 'react-helmet';
import EquipmentTypeModal from '../../components/EquipmentTypeModal';
import DashboardHeader from '../../components/DashboardHeader';
import Input from '../../components/Input';
import Plus from '../../SVG/Plus';
import EditEquipmentForm from '../../components/EditEquipmentForm';
import {setCategories} from '../../store/builder/equipmentEditSlice';
import log from '../../SVG/Log';
import Select from 'react-select';
import {useGetRevisions} from '../EquipmentType/model/revisions/getRevisions';
import {useSetCurrentRevision} from '../EquipmentType/model/revisions/setCurrentRevision';
import {useTranslation} from '../../hooks/useTranslation';

const SELECT_STYLED = {
  control: (baseStyles, state) => ({
    ...baseStyles,
    borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
    borderRadius: 8,
    height: 42,
    backgroundColor: '#F4F5F6',
    marginTop: 8,
    width: '100%',
  }),
};

export default function EquipmentTypeEditNew({editType}) {
  const {t} = useTranslation();
  const params = useParams();
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [getRevisions, revisionOptions, current_revision] = useGetRevisions();
  const [handleSelect] = useSetCurrentRevision();
  const filterOptions = revisionOptions.filter(el => el.code !== '');

  useEffect(() => {
    if (!current_revision) {
      getRevisions(searchParams.get('revision'));
    }
  }, []);

  const categories = useSelector(
    state => state?.rootReducer?.equipmentEditSlice,
  );
  const currentCode = useSelector(
    state => state?.rootReducer?.revisionsSlice?.current_option?.code,
  );

  const [
    getCurrentInspection,
    {
      data: inspectionCurrentData,
      isError: inspectionCurrentIsError,
      error: inspectionCurrentError,
      isSuccess: inspectionCurrentIsSuccess,
      isLoading: inspectionCurrentIsLoading,
    },
  ] = useLazyGetCurrentInspectionQuery();
  const [
    getCurrentFacilityInspection,
    {
      data: facilityInspectionCurrentData,
      isError: facilityInspectionCurrentIsError,
      error: facilityInspectionCurrentError,
      isSuccess: facilityInspectionCurrentIsSuccess,
      isLoading: facilityInspectionCurrentIsLoading,
    },
  ] = useLazyGetCurrentFacilityInspectionsQuery();

  const [
    refuseInspections,
    {
      data: refuseInspectionsData,
      isError: refuseInspectionsIsError,
      error: refuseInspectionsError,
      isSuccess: refuseInspectionsIsSuccess,
      isLoading: refuseInspectionsIsLoading,
    },
  ] = useRefuseInspectionsMutation();

  const [modalStatus, setModalStatus] = useState(false);

  const [
    getInspection,
    {
      data: inspectionData,
      isError: inspectionIsError,
      error: inspectionError,
      isSuccess: inspectionIsSuccess,
      isLoading: inspectionIsLoading,
    },
  ] = useLazyGetInspectionsByTypeQuery();

  const [
    update,
    {
      data: updateData,
      isError: updateIsError,
      error: updateError,
      isSuccess: updateIsSuccess,
      isLoading: updateIsLoading,
    },
  ] = useUpdateEquipmentTypeMutation();

  const [
    updateFacilityInfo,
    {
      data: updateFacilityInfoData,
      isError: updateFacilityInfoIsError,
      error: updateFacilityInfoError,
      isSuccess: updateFacilityInfoIsSuccess,
      isLoading: updateFacilityInfoIsLoading,
    },
  ] = useUpdateFacilityInfoMutation();

  const [
    saveEquipmentForm,
    {
      data: saveEquipmentFormData,
      isError: saveEquipmentFormIsError,
      error: saveEquipmentFormError,
      isSuccess: saveEquipmentFormIsSuccess,
      isLoading: saveEquipmentFormIsLoading,
    },
  ] = useSaveEquipmentFormMutation();

  const [errorHandler, setErrorHandler] = useErrorHandler([
    updateFacilityInfoError,
    inspectionCurrentError,
    updateError,
    inspectionError,
    refuseInspectionsError,
    facilityInspectionCurrentError,
  ]);

  useEffect(() => {
    if (current_revision) {
      dispatch(setLoader(true));
      getCurrentInspection(`${params.id}/?revision=${current_revision?.code}`)
        .unwrap()
        .then(res => {
          dispatch(
            setCategories(JSON.parse(JSON.stringify(res?.data?.form_groups))),
          );
          dispatch(setLoader(false));
        })
        .catch(err => {
          dispatch(setLoader(false));
        });
    }
  }, [current_revision, currentCode]);

  useEffect(() => {
    if (inspectionCurrentIsLoading) {
      dispatch(setLoader(true));
    }
    if (inspectionCurrentIsSuccess) {
      dispatch(setLoader(false));
      dispatch(
        setCategories(
          JSON.parse(JSON.stringify(inspectionCurrentData?.data?.form_groups)),
        ),
      );
    }
  }, [inspectionCurrentIsLoading, inspectionCurrentIsSuccess]);
  useEffect(() => {
    if (saveEquipmentFormIsLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }

    if (saveEquipmentFormIsSuccess) {
      dispatch(setLoader(false));
      toast.success(saveEquipmentFormData.message);
    }
  }, [saveEquipmentFormIsLoading, saveEquipmentFormIsSuccess]);

  const saveForm = () => {
    saveEquipmentForm({id: params?.id, form_groups: categories?.categories});
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('equipmentTypeEditNew.pageTitle')}</title>
      </Helmet>
      {/*{modalStatus && (*/}
      {/*  <EquipmentTypeModal*/}
      {/*    exit={() => setModalStatus(false)}*/}
      {/*    list={inspectionData.data}*/}
      {/*    comparisonList={*/}
      {/*      editType === "facility"*/}
      {/*        ? facilityInspectionCurrentData?.data?.links*/}
      {/*        : inspectionCurrentData?.data?.links*/}
      {/*    }*/}
      {/*    setAddedInspections={setAddedInspections}*/}
      {/*    addedInspections={addedInspections}*/}
      {/*    pickedInspection={pickedInspection}*/}
      {/*    setPickedInspection={setPickedInspection}*/}
      {/*    saveEquipment={() => saveEquipment()}*/}
      {/*  />*/}
      {/*)}*/}
      <DashboardHeader
        title={t('equipmentTypeEditNew.title')}
        type={'equipment-edit'}
        action={saveForm}
        cancelNav={'/equipment-type'}
      />

      <div className='editType__revision'></div>
      <div className='editType'>
        <div className='editType__box'>
          <p className='editType__equipmentLabel'>{t('equipmentTypeEditNew.equipmentType')}</p>
          <div className='editType__equipmentName'>
            {inspectionCurrentData?.data?.title}
          </div>
        </div>
        <div className='editType__box border-none'>
          <p className='editType__equipmentLabel'>{t('equipmentTypeEditNew.inspectionsRevision')}</p>
          <Select
            options={filterOptions}
            styles={SELECT_STYLED}
            value={filterOptions.find(opt => opt.code === current_revision?.code) || null}
            onChange={async selected => {
              await handleSelect(selected);
              await navigate(
                '/equipment-type/edit/' +
                  params.id +
                  `?revision=${selected?.code}`,
              );
            }}
          />
        </div>
        <div>
          {inspectionCurrentData?.data?.form_groups?.map(category => (
            <div className='editType__category' key={category?.key}>
              <p>{category?.name}</p>
              <div className='editType__forms'>
                {category.forms.map((form, index) => (
                  <EditEquipmentForm
                    disabled={!inspectionCurrentData?.data?.can_edit}
                    key={index}
                    form={form}
                    index={form.id}
                    category={category}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
        {/*<div className="editType__btn" onClick={() => setModalStatus(true)}>*/}
        {/*  <Plus color={"#4268B0"} />*/}
        {/*  <span>Add Inspection</span>*/}
        {/*</div>*/}
      </div>
    </Wrapper>
  );
}
